import { useState, useEffect, useMemo } from 'react';
import queryString from 'query-string';
import Swal from 'sweetalert2';
import guestService from 'src/services/api/guest';
import EventService from 'src/services/api/eventService';
import crecerFormsInstance from 'src/services/api/crecer';
import LoginStep from 'src/containers/crecer/autoAsistencia/LoginStep';
import FiltersStep from 'src/containers/crecer/autoAsistencia/FiltersStep';
import DatesStep from 'src/containers/crecer/autoAsistencia/DatesStep';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import {
  getDefaultInputs,
  getCurrentEvent,
  getStepsMadeForEvent,
  getDatesForEvent,
  getSelectedCodesList,
  hasAttendanceForDate,
  buildLoginErrorInputs,
} from 'src/containers/crecer/autoAsistencia/autoAsistenciaHelpers';

export default function CrecerAutoAsistencia() {
  const parsedQueryString = queryString.parse(
    typeof window !== 'undefined' ? window.location.search : ''
  );

  const [step, setStep] = useState('login');
  const [inputs, setInputs] = useState(getDefaultInputs);
  const [errorInputs, setErrorInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [guest, setGuest] = useState(null);
  const [crecerDates, setCrecerDates] = useState([]);
  const [selectedAttendanceCodes, setSelectedAttendanceCodes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const getCaptchaToken = useRecaptchaToken('crecer_attendance');

  useEffect(() => {
    const id = parsedQueryString.identification?.trim();
    const phone = parsedQueryString.phone?.trim();
    if (id || phone) {
      setInputs((prev) => ({
        ...prev,
        ...(id && { identification: id }),
        ...(phone && { phone }),
      }));
    }
  }, []);

  useEffect(() => {
    EventService.getDates()
      .then((res) => {
        if (res?.data?.success && res.data.dates) setCrecerDates(res.data.dates);
      })
      .catch(() => {});
  }, []);

  const currentEvent = useMemo(
    () => getCurrentEvent(inputs),
    [inputs.type, inputs.step, inputs.seminary]
  );
  const stepsMadeForEvent = useMemo(
    () => getStepsMadeForEvent(guest, inputs, currentEvent),
    [guest, inputs.type, currentEvent]
  );
  const datesForEvent = useMemo(
    () => getDatesForEvent(crecerDates, currentEvent),
    [crecerDates, currentEvent]
  );
  const selectedCodesList = useMemo(
    () => getSelectedCodesList(selectedAttendanceCodes),
    [selectedAttendanceCodes]
  );

  const captchaRequired = isCaptchaRequired();

  const verifyLoginForm = () => {
    const { errorInputs: err, hasErrors } = buildLoginErrorInputs(inputs);
    setErrorInputs(err);
    return hasErrors;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (verifyLoginForm()) return;
    setLoading(true);

    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      setLoading(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    const body = {
      eventSlugname: 'crecerAttendance',
      ...(captchaRequired ? { captcha } : {}),
    };
    if (inputs.whoSentTheLink?.trim()) {
      body.whoSentTheLink = inputs.whoSentTheLink.trim();
    }
    try {
      const response = await guestService.getGuest(
        `${inputs.identification}/${inputs.phone}`,
        'crecer/crecerAttendance',
        body
      );
      if (response?.data?.success && response.data.Attendant) {
        setGuest(response.data.Attendant);
        setStep('filters');
      } else {
        Swal.fire(response?.data?.message || 'No se encontró el perfil.');
      }
    } catch (error) {
      Swal.fire(error?.response?.data?.message || 'Error al cargar tus datos.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (e, { name, value }) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
    setSelectedAttendanceCodes({});
  };

  const hasAttendance = (date) => hasAttendanceForDate(stepsMadeForEvent, date);

  const toggleDateSelection = (attendanceCode) => {
    setSelectedAttendanceCodes((prev) => ({
      ...prev,
      [attendanceCode]: !prev[attendanceCode],
    }));
  };

  const handleMarkAttendance = async () => {
    if (selectedCodesList.length === 0) {
      Swal.fire('Selecciona al menos una fecha para marcar asistencia.');
      return;
    }

    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        identification: inputs.identification,
        phone: inputs.phone,
        whoSentTheLink: inputs.whoSentTheLink?.trim() || undefined,
        event: currentEvent,
        type: inputs.type,
        attendanceCodes: selectedCodesList,
      };
      if (captchaRequired) {
        body.captcha = captcha;
      }
      await crecerFormsInstance.markSelfAttendance(body);
      setStep('login');
      setSelectedAttendanceCodes({});
      Swal.fire('Listo', 'Tu asistencia ha sido registrada correctamente.', 'success');
    } catch (err) {
      Swal.fire(err?.response?.data?.message || 'Error al registrar la asistencia.');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'login') {
    return (
      <LoginStep
        inputs={inputs}
        setInputs={setInputs}
        errorInputs={errorInputs}
        setErrorInputs={setErrorInputs}
        loading={loading}
        onSubmit={handleLoginSubmit}
      />
    );
  }

  if (step === 'filters') {
    return (
      <FiltersStep
        inputs={inputs}
        currentEvent={currentEvent}
        onSelectChange={handleSelectChange}
        onContinue={() => setStep('dates')}
      />
    );
  }

  if (step === 'dates') {
    return (
      <DatesStep
        inputs={inputs}
        datesForEvent={datesForEvent}
        stepsMadeForEvent={stepsMadeForEvent}
        selectedAttendanceCodes={selectedAttendanceCodes}
        selectedCodesList={selectedCodesList}
        submitting={submitting}
        hasAttendance={hasAttendance}
        toggleDateSelection={toggleDateSelection}
        onMarkAttendance={handleMarkAttendance}
        onChangeCourse={() => setStep('filters')}
      />
    );
  }

  return null;
}
